import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { AppConfig } from "@src/config/AppConfig";
import { GetMasterDataProductCategoriesDTO } from "@src/domain/dto/masterData";
import type { MasterDataProductCategory } from "@src/domain/entities/masterData";
import { GetMasterDataProductCategoriesUseCase } from "@src/application/useCases/masterData";
import { MasterDataProductCategoryRepository } from "@src/infrastructure/repositories/masterData";
import { useMasterDataListQuery } from "./masterData/useMasterDataListQuery";

interface MasterDataProductCategoriesDataContextValue {
  brandKey: string;
  productCategories: MasterDataProductCategory[];
  loading: boolean;
  error: string | null;
  search: string;
  setSearch: (search: string) => void;
  page: number;
  limit: number;
  total: number;
  setPage: (page: number) => void;
  refetch: () => void;
}

const MasterDataProductCategoriesDataContext =
  createContext<MasterDataProductCategoriesDataContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useMasterDataProductCategories(): MasterDataProductCategoriesDataContextValue {
  const context = useContext(MasterDataProductCategoriesDataContext);
  if (!context) {
    throw new Error(
      "useMasterDataProductCategories must be used within MasterDataProductCategoriesDataProvider",
    );
  }
  return context;
}

interface MasterDataProductCategoriesDataProviderProps {
  brandKey: string;
  children: ReactNode;
  defaultPageSize?: number;
}

export function MasterDataProductCategoriesDataProvider({
  brandKey,
  children,
  defaultPageSize = AppConfig.getDefaultPaginationLimit(),
}: Readonly<MasterDataProductCategoriesDataProviderProps>) {
  const getProductCategoriesUseCase = useMemo(
    () =>
      new GetMasterDataProductCategoriesUseCase(
        new MasterDataProductCategoryRepository(),
      ),
    [],
  );

  const fetchPage = useCallback(
    async (args: { search: string | null; page: number; limit: number }) =>
      getProductCategoriesUseCase.execute(
        new GetMasterDataProductCategoriesDTO({
          includeInactive: true,
          search: args.search,
          page_number: args.page,
          page_size: args.limit,
        }),
      ),
    [getProductCategoriesUseCase],
  );

  const {
    items: productCategories,
    loading,
    error,
    search,
    setSearch,
    page,
    setPage,
    limit,
    total,
    refetch,
  } = useMasterDataListQuery({
    brandKey,
    defaultPageSize,
    errorMessage: "Failed to load master data product categories",
    cacheScope: "product-categories",
    fetchPage,
  });

  const value = useMemo(
    (): MasterDataProductCategoriesDataContextValue => ({
      brandKey,
      productCategories,
      loading,
      error,
      search,
      setSearch,
      page,
      limit,
      total,
      setPage,
      refetch,
    }),
    [
      brandKey,
      productCategories,
      loading,
      error,
      search,
      setSearch,
      page,
      limit,
      total,
      setPage,
      refetch,
    ],
  );

  return (
    <MasterDataProductCategoriesDataContext.Provider value={value}>
      {children}
    </MasterDataProductCategoriesDataContext.Provider>
  );
}
